import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './AiChat.module.scss';

export interface ThinkingIndicatorProps {
  label: string;
  step?: number;
  /** Without avatar the line indents to align with a message rendered above it. */
  withAvatar?: boolean;
  /** Compact widget: typing dots inside an assistant bubble instead of the avatar line. */
  compact?: boolean;
}

/**
 * Animated status line shown while the agent works: spinner avatar plus a
 * shimmering text that swaps with each backend tool event. Disappears entirely
 * once the answer arrives (no collapsible step log by design).
 */
const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ label, step, withAvatar = true, compact = false }) => {
  const { t } = useTranslation();
  if (compact) {
    return (
      <div className={classNames(styles.bubble, styles.bubbleAssistant, styles.typing)} aria-label={label}>
        <span className={styles.typingDots}>
          <span />
          <span />
          <span />
        </span>
        <span key={label} className={styles.statusLine}>
          {label}
        </span>
      </div>
    );
  }
  return (
    <div className="self-start flex items-start gap-12">
      {withAvatar && (
        <div className="w-[28px] h-[28px] shrink-0 rounded-base surface-primary-gradient flex items-center justify-center text-white">
          <svg className={styles.spinner} width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.8" />
            <path d="M7 2a5 5 0 0 1 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
      )}
      <div className={classNames('flex flex-col gap-4 pt-5', !withAvatar && 'pl-40')}>
        <span key={label} className={styles.statusLine}>
          {label}
        </span>
        {step != null && <span className="typo-text-12 text-faded">{t('tracking.ai_chat.step', { step })}</span>}
      </div>
    </div>
  );
};

export default ThinkingIndicator;
